/**
 * @internal true
 * @module briefing/BriefingChangelog
 * Widget de novidades do sistema — exibe atualizações, melhorias,
 * correções e notas de versão do Aluno Conectado.
 *
 * P3: Inicialmente com dados estáticos locais.
 * Futuro: endpoint GET /api/v1/sistema/changelog
 */
import { Sparkles, ChevronRight, Bug, Zap, Star, Shield, BookOpen } from 'lucide-react';

interface ChangelogEntry {
  id: string;
  tipo: 'feature' | 'fix' | 'improvement' | 'security' | 'docs';
  titulo: string;
  descricao: string;
  data: string;
  versao?: string;
}

// ─── Dados estáticos até o backend existir ───
const CHANGELOG_DATA: ChangelogEntry[] = [
  {
    id: '1',
    tipo: 'feature',
    titulo: 'Dashboard Briefing Diário',
    descricao: 'Nova página de entrada com visão consolidada do dia: agenda, alertas, equipe e mural TV.',
    data: new Date().toISOString(),
    versao: 'v2.8',
  },
  {
    id: '2',
    tipo: 'feature',
    titulo: 'Chat Interno entre Equipe',
    descricao: 'Widget de chat flutuante integrado em todos os dashboards para comunicação rápida entre membros.',
    data: new Date(Date.now() - 86400000).toISOString(),
    versao: 'v2.7',
  },
  {
    id: '3',
    tipo: 'improvement',
    titulo: 'Dark Mode Aprimorado',
    descricao: 'Modo escuro aplicado em todos os formulários, tabelas e modais com transição suave.',
    data: new Date(Date.now() - 172800000).toISOString(),
    versao: 'v2.7',
  },
  {
    id: '4',
    tipo: 'fix',
    titulo: 'Correção no Registro de Chamada',
    descricao: 'Resolvido problema de duplicação ao registrar presença de alunos transferidos.',
    data: new Date(Date.now() - 259200000).toISOString(),
    versao: 'v2.6',
  },
  {
    id: '5',
    tipo: 'security',
    titulo: 'Passkey com Windows Hello',
    descricao: 'Autenticação biométrica integrada para login rápido e seguro via Windows Hello.',
    data: new Date(Date.now() - 345600000).toISOString(),
    versao: 'v2.5',
  },
];

const TIPO_CONFIG: Record<string, { icon: typeof Sparkles; color: string; label: string }> = {
  feature:     { icon: Star,     color: '#8b5cf6', label: 'Novo' },
  fix:         { icon: Bug,      color: '#ef4444', label: 'Correção' },
  improvement: { icon: Zap,      color: '#3b82f6', label: 'Melhoria' },
  security:    { icon: Shield,   color: '#22c55e', label: 'Segurança' },
  docs:        { icon: BookOpen, color: '#f97316', label: 'Docs' },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

export function BriefingChangelog() {
  return (
    <div className="brief-card">
      <div className="brief-card-head">
        <h3 className="brief-card-title">
          <Sparkles style={{ color: '#f59e0b' }} />
          Novidades do Sistema
        </h3>
        <button className="brief-card-link" onClick={() => {}}>
          Histórico <ChevronRight size={12} />
        </button>
      </div>
      <div className="brief-card-body">
        <div className="brief-agenda-timeline">
          {CHANGELOG_DATA.map((entry) => {
            const cfg = TIPO_CONFIG[entry.tipo] || TIPO_CONFIG.improvement;
            const Icon = cfg.icon;

            return (
              <div
                key={entry.id}
                className="brief-agenda-event"
                style={{ borderLeftColor: cfg.color, cursor: 'default' }}
              >
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  minWidth: 48, gap: 2,
                }}>
                  <span style={{
                    fontSize: '0.58rem', fontWeight: 800,
                    color: cfg.color,
                    background: `${cfg.color}15`,
                    padding: '2px 6px',
                    borderRadius: 4,
                    textTransform: 'uppercase',
                  }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: '0.6rem', color: 'var(--text-muted, #94a3b8)' }}>
                    {formatDate(entry.data)}
                  </span>
                </div>
                <div className="brief-agenda-info">
                  <p className="brief-agenda-info-title" style={{ whiteSpace: 'normal' }}>{entry.titulo}</p>
                  <p className="brief-agenda-info-sub" style={{ whiteSpace: 'normal', lineHeight: 1.4 }}>
                    {entry.descricao}
                  </p>
                </div>
                {entry.versao && (
                  <span style={{
                    fontSize: '0.6rem', fontWeight: 700,
                    color: 'var(--text-muted, #94a3b8)',
                    background: 'var(--bg-body, #f1f5f9)',
                    padding: '2px 7px',
                    borderRadius: 6,
                    alignSelf: 'flex-start',
                    flexShrink: 0,
                  }}>
                    {entry.versao}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
